import React, { useState, useEffect } from "react";
import { useLazyGetPageContentQuery } from "../api/businessApi";
import PageSectionEditor from "./PageSectionEditor";

const PAGES = [
  { key: "home", label: "Home", icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
  { key: "about", label: "About", icon: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" },
  { key: "advisory", label: "Advisory", icon: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" },
];

const PageContentManagement = () => {
  const [selectedPage, setSelectedPage] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [getPageContent, { data: pageData, isLoading, isError, error }] = useLazyGetPageContentQuery();

  useEffect(() => {
    if (selectedPage) {
      getPageContent(selectedPage);
    }
  }, [selectedPage, getPageContent]);

  const handleEditSection = (section) => {
    setEditingSection(section);
    setEditorOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingSection(null);
  };

  const handleEditorSuccess = () => {
    // Refetch the page content
    if (selectedPage) {
      getPageContent(selectedPage);
    }
  };

  // Get label for a section from schema or format the key
  const getSectionLabel = (section) => {
    if (pageData?.schema?.[section.section_key]?.label) {
      return pageData.schema[section.section_key].label;
    }
    return section.section_key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Get a brief preview of the content
  const getContentPreview = (content) => {
    if (!content || typeof content !== "object") return "No content";
    const entries = Object.entries(content);
    if (entries.length === 0) return "Empty content";

    const previews = [];
    for (const [key, value] of entries.slice(0, 3)) {
      if (typeof value === "string") {
        previews.push(`${key}: "${value.substring(0, 50)}${value.length > 50 ? "..." : ""}"`);
      } else if (Array.isArray(value)) {
        previews.push(`${key}: ${value.length} item${value.length !== 1 ? "s" : ""}`);
      } else if (typeof value === "object" && value !== null) {
        previews.push(`${key}: {${Object.keys(value).length} fields}`);
      }
    }
    if (entries.length > 3) {
      previews.push(`+${entries.length - 3} more fields`);
    }
    return previews.join(" | ");
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left Sidebar - Page List */}
      <div className="lg:w-64 flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Pages
        </h3>
        <div className="space-y-2">
          {PAGES.map((page) => (
            <button
              key={page.key}
              onClick={() => setSelectedPage(page.key)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-all duration-200 ${
                selectedPage === page.key
                  ? "bg-rose-50 border-rose-300 text-rose-700 shadow-sm"
                  : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
              }`}
            >
              <svg
                className={`w-5 h-5 flex-shrink-0 ${
                  selectedPage === page.key ? "text-rose-600" : "text-gray-400"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={page.icon} />
              </svg>
              <span className="font-medium text-sm">{page.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Right Content - Sections */}
      <div className="flex-1 min-w-0">
        {!selectedPage && (
          <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-500 mb-2">Select a Page</h3>
            <p className="text-sm text-gray-400">Choose a page from the left to manage its content sections.</p>
          </div>
        )}

        {selectedPage && isLoading && (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-5 animate-pulse">
                <div className="h-5 bg-gray-200 rounded w-1/3 mb-3" />
                <div className="h-4 bg-gray-100 rounded w-2/3" />
              </div>
            ))}
          </div>
        )}

        {selectedPage && isError && (
          <div className="text-center py-12 bg-red-50 rounded-xl border border-red-200">
            <svg className="w-12 h-12 text-red-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h3 className="text-lg font-medium text-red-700 mb-1">Failed to load sections</h3>
            <p className="text-sm text-red-500">{error?.data?.message || "An error occurred."}</p>
            <button
              onClick={() => getPageContent(selectedPage)}
              className="mt-4 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {selectedPage && !isLoading && !isError && pageData?.sections && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {PAGES.find((p) => p.key === selectedPage)?.label || selectedPage} Sections
              </h3>
              <span className="text-sm text-gray-500">
                {pageData.sections.length} section{pageData.sections.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="space-y-3">
              {pageData.sections.map((section) => (
                <div
                  key={section.id || section.section_key}
                  className="bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all duration-200"
                >
                  <div className="flex items-start justify-between p-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-gray-900">
                          {getSectionLabel(section)}
                        </h4>
                        {!section.is_active && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-2 font-mono">
                        {section.section_key}
                      </p>
                      <p className="text-sm text-gray-600 truncate">
                        {getContentPreview(section.content)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleEditSection(section)}
                      className="ml-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors flex-shrink-0"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {pageData.sections.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <p className="text-gray-500">No sections found for this page.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section Editor Modal */}
      {editingSection && (
        <PageSectionEditor
          isOpen={editorOpen}
          onClose={handleEditorClose}
          pageKey={selectedPage}
          sectionKey={editingSection.section_key}
          label={getSectionLabel(editingSection)}
          content={editingSection.content}
          onSuccess={handleEditorSuccess}
        />
      )}
    </div>
  );
};

export default PageContentManagement;
